import Vuex from "vuex"
import { setAid } from "../plugins/env"
import { getAccountInfo, getIsOnline } from "@/api/auth"
import { getComm, getStoreInfo } from "@/api/comm"
import { getClickCollectStores } from "@/api/delivery"
import { Logger } from "@/plugins/logger"
import { resetLocal, getRequestHeaders, getLoginStatus, getDeliveryMethod } from "@/utils/utils"
import { setCommonParam, setCommonReqParam, setIsOnline } from "@/plugins/env"
import storage from "@/utils/storage"

const updateSelStore = async $cookies => {
  try {
    const curSelStore = storage.get("curSelStore")
    // 门店不存在 || 无erpStoreId(自提门店)字段 || 配送模式
    if (!curSelStore || !("erpStoreId" in curSelStore) || getDeliveryMethod() === "homeDelivery") {
      return
    }
    const locale = $cookies.get("superweb-locale") || "zh_HK"
    if (locale === curSelStore._locale) {
      return
    }
    const { data: res } = await getClickCollectStores({ param: JSON.stringify({ erpStoreId: curSelStore.erpStoreId }) })
    const target = (res || []).find(item => item.erpStoreId === curSelStore.erpStoreId)
    if (target) {
      target._locale = locale
      storage.set("curSelStore", target)
    }
  } catch (error) {
    console.log(error)
  }
}

const store = () =>
  new Vuex.Store({
    state: {
      token: "",
      lang: "",
      commonParams: null,
      accountInfo: null,
      storeInfo: null,
      isServerInit: false,
      // 作为服务端部分cookie无法即时更新补充
      resCookies: {
        store: "",
        longitude: "",
        latitude: ""
      },
      domainFlag: null,
      isOnline: false,
      aid: ""
    },
    mutations: {
      setToken(state, token) {
        state.token = token
      },
      setLang(state, lang) {
        state.lang = lang
      },
      setIsServerInit(state, isServerInit) {
        state.isServerInit = isServerInit
      },
      setCommonParams(state, commonParams) {
        state.commonParams = commonParams
      },
      setResCookies(state, resCookies) {
        state.resCookies = resCookies
      },
      setStoreInfo(state, storeInfo) {
        state.storeInfo = storeInfo
      },
      setAccountInfo(state, accountInfo) {
        state.accountInfo = accountInfo
      },
      setCascaderData(state, data) {
        state.cascaderData = data
      },
      setDomainFlag(state, domainFlag) {
        state.domainFlag = domainFlag
      },
      setIsOnline(state, isOnline) {
        state.isOnline = isOnline
      },
      setAid(state, aid) {
        state.aid = aid
      }
    },
    actions: {
      getCommonParamsSet(store) {
        const { $cookies } = this
        return {
          comm: setCommonParam({ store, $cookies }),
          commonReqParams: setCommonReqParam({ store, $cookies })
        }
      },
      /**
       * 作为走服务端初始化后的数据还原
       * @param {*} store
       */
      async setStorageData(store) {
        const { accountInfo } = store.state
        const commData = await store.dispatch("getCommonParamsSet")
        if (accountInfo) {
          storage.set("accountInfo", accountInfo, { useBase64: true })
        }
        storage.set("commData", commData)
      },
      async bootstrapData(store, { req }) {
        try {
          const { $cookies } = this
          const { commit, dispatch, state } = store
          let { commonParams } = state
          // getIsOnline
          let { data: isOnline } = await getIsOnline(
            {},
            {
              headers: getRequestHeaders({ req, store })
            }
          )
          isOnline = !!isOnline
          commit("setIsOnline", isOnline)
          setIsOnline(isOnline)

          const pickUpStoreId = $cookies.get("pickUpStoreId") || commonParams?.pickUpStoreId
          const shipmentType = $cookies.get("shipmentType") || commonParams?.shipmentType
          let accountInfo = storage.get("accountInfo", { useBase64: true }) || null
          let storeInfo = {}
          let defaultAddress = null
          if (!commonParams) {
            commonParams = await dispatch("getCommonParams", {
              params: {
                shipmentType,
                pickUpStoreId
              }
            })
          }

          // 初始化构造comm参数
          setCommonParam({ store, $cookies })

          if (isOnline) {
            // 用户信息不存在
            if (!accountInfo) {
              // 服务端和客户端都检查，确保 localstorage 中存在 accountInfo
              accountInfo = await dispatch("getLoginInfo", req)
              // 门店id和经纬度放在cookie中，没有会通过服务端获取
              if (process.server && shipmentType === 1) {
                if (accountInfo?.address?.length) {
                  // cookie aid 为地址id
                  // 优先级: aid > default address > address first

                  defaultAddress =
                    accountInfo.address.find(item => item.priority == "Default") || accountInfo.address[0]
                  const aid = $cookies.get("aid")
                  if (aid) {
                    setAid(aid)
                    const index = accountInfo.address.findIndex(item => item.id == aid)
                    if (index > -1) {
                      defaultAddress = accountInfo.address[index]
                    }
                  }
                  storeInfo = await dispatch("getStoreInfo", {
                    defaultAddress,
                    $cookies,
                    headers: getRequestHeaders({ req, store })
                  })
                }
              }
            }
            updateSelStore(this.$cookies)
          } else {
            process.client && resetLocal()
            accountInfo = null
          }

          // 设置门店、用户信息等数据
          commit("setStoreInfo", storeInfo)
          commit("setAccountInfo", accountInfo)
          commit("setIsServerInit", process.server)

          // 公共环境变量同步
          const result = await dispatch("getCommonParamsSet")

          // 缓存到本地
          // TODO 待commData不再使用，删除
          if (process.client) {
            storage.set("commData", result)
          }

          return result
        } catch (error) {
          Logger.error("加载初始数据失败", error)
        }
      },
      /**
       * 获取公共参数
       */
      async getCommonParams(store, { params = {}, req, $cookies }) {
        const { commit, state } = store
        const commonParams = state.commonParams
        if (!commonParams) {
          const headers = getRequestHeaders({ req, store })
          try {
            const { data: commonParams } = await getComm(
              {
                param: JSON.stringify(params)
              },
              {
                headers
              }
            )
            // 设置初始默认值
            commonParams.pickUpStoreId = $cookies.get("pickUpStoreId") || commonParams.pickUpStoreId || ""
            commonParams.shipmentType = $cookies.get("shipmentType") || commonParams.shipmentType || 1
            commonParams.storeId = $cookies.get("store") || commonParams.storeId || ""
            if (process.server) {
              ;["pickUpStoreId", "shipmentType", "venderId"].forEach(key => {
                if ($cookies.get(key)) {
                  return
                }
                $cookies.set(key, commonParams[key])
              })
              if (!$cookies.get("longitude")) {
                $cookies.set("longitude", commonParams.deliveryLng)
                $cookies.set("latitude", commonParams.deliveryLat)
              }
              if (commonParams.shipmentType === 2 || !$cookies.get("store")) {
                $cookies.set("store", commonParams.storeId)
              }
            }
            commit("setCommonParams", commonParams)
            return commonParams
          } catch (error) {
            Logger.error("获取公共参数失败", error)
            return null
          }
        }
        return commonParams
      },
      /**
       * 获取登录信息
       */
      async getLoginInfo(store, req) {
        const { isOnline } = getLoginStatus()
        if (isOnline) {
          try {
            const { data } = await getAccountInfo(
              {},
              {
                headers: getRequestHeaders({ req, store })
              }
            )
            if (data) {
              storage.set("accountInfo", data, { useBase64: true })
            }
            return data
          } catch (error) {
            Logger.error("加载用户信息失败", error)
          }
        }
      },

      /**
       * 获取门店信息 门店id和经纬度放在cookie中，没有会通过服务端获取
       */
      async getStoreInfo(store, { defaultAddress = {}, $cookies, headers }) {
        try {
          const { lng, lat } = defaultAddress
          const param = {
            longitude: lng,
            latitude: lat,
            aid: defaultAddress.id
          }
          // cookies 中使用地址的经纬度
          $cookies.set("longitude", lng)
          $cookies.set("latitude", lat)
          const { data } = await getStoreInfo({ param }, { headers })
          $cookies.set("store", data?.storeId || -1)
          return data
        } catch (error) {
          Logger.error("加载门店信息失败", error)
        }
      }
    }
  })

export default store
